<template>
  <div>
    <CustomCard
      :header="`BILLINGS`"
      subHeader="List of all registered billings.">
      <template #card-header-filter>
        <div class="month-filter">
          <b-badge
            class="billing-period__status"
            :variant="billingPeriodStatus ? 'success' : 'danger'">
            {{ billingPeriodStatus ? 'ACTIVE' : 'INACTIVE' }}
          </b-badge>
          <span class="mr-2">Period: </span>
          <v-select
            class="billing-period__select"
            :options="options.billingPeriods.items"
            :disabled="options.billingPeriods.isBusy"
            :loading="options.billingPeriods.isBusy"
            label="month"
            :filterBy="filterBillingPeriod"
            v-model="selectedBillingPeriod"
            @input="onChangeBillingPeriod"
          >
            <template #selected-option="{ month, year }">
              {{ month.name + ' ' + year }}
            </template>
            <template #option="{ month, year }">
              {{ (month && month.name || '' ) + ' ' + year }}
            </template>
            <template #spinner="{ loading }">
              <div v-if="loading" style="border-left-color: rgba(88,151,251,0.71)" class="vs__spinner" />
            </template>
          </v-select>
        </div>
      </template>
      <template v-slot:body>
        <Full
          @create="setCreate()"
          :noCreateButton="!isAccessible($options.BillingPermissions.ADD.id) || !billingPeriodStatus"
          placeholder="Search Here..."
          :isServerSide="true"
          @search="loadBillings()"
          @paginate="loadBillings()"
          :filter="filters.billing"
          :table="tables.billings"
          :pagination="paginations.billing"
          :sortChanged="sortChanged"
          buttonLabel="Create New Billing">
          <template #other-filter>
            <b-button
              v-if="billingPeriodStatus"
              size="sm"
              @click="setCreateBatchBilling()"
              variant="outline-primary">
              <v-icon
                name="plus-circle" />
              Create Batch Billing
            </b-button>
          </template>
          <template v-slot:cell(action)="data">
            <b-dropdown
              boundary="window"
              right
              variant="link"
              toggle-class="text-decoration-none"
              no-caret>
              <template v-slot:button-content>
                <v-icon name="ellipsis-v" />
              </template>
              <b-dropdown-item
                @click="onToggleDetails(data)">
                {{ data.detailsShowing ? 'Hide Details' : 'View Details' }}
              </b-dropdown-item>
              <b-dropdown-item
                v-if="isAccessible($options.BillingPermissions.EDIT.id)"
                @click="setUpdate(data.item)"
                :disabled="showModalEntry">
                Edit
              </b-dropdown-item>
              <b-dropdown-item
                v-if="isAccessible($options.BillingPermissions.DELETE.id) && billingPeriodStatus"
                @click="setDelete(data.item.id)"
                :disabled="showModalConfirmation">
                Delete
              </b-dropdown-item>
              <b-dropdown-item
                @click="onPreview(data.item.id)">
                Preview
              </b-dropdown-item>
            </b-dropdown>
          </template>
          <template v-slot:head(client) >
            <CustomToolTip id="client-icon" label="CLIENT">
              <template #body>
              <div>
                <div>Line 1 - Represents the client name.</div>
                <div class="row__sub-label">Line 2 - Represents the client code.</div>
              </div>
              </template>
            </CustomToolTip>
          </template>
          <template v-slot:head(contract) >
            <CustomToolTip id="contract-icon" label="CONTRACT">
              <template #body>
                <div>
                  <div>Line 1 - Represents the tradename.</div>
                  <div class="row__sub-label">Line 2 - Represents the contract code.</div>
                </div>
              </template>
            </CustomToolTip>
          </template>

          <template v-slot:cell(client)="data">
            <div>{{ data.item.client.name }}</div>
            <div class="row__sub-label">{{ data.item.client.code }}</div>
          </template>
          <template v-slot:cell(contract)="data">
            <div>{{ data.item.contract.tradeName }}</div>
            <div class="row__sub-label">{{ data.item.contract.contractNo }}</div>
          </template>

          <template v-slot:cell(period)="data">
            <div>{{ `${$options.Months.getName(data.item.monthId)} ${data.item.year}` }}</div>
          </template>

          <template v-slot:cell(billingNo)="data">
            <b-link @click="onToggleDetails(data)">{{ data.item.billingNo }}</b-link>
          </template>

          <template #row-details="data">
            <BillingRowDetail :billing="data.item" :loading="rowLoading" />
          </template>
        </Full>
      </template>
    </CustomCard>

    <BillingEntry
      v-if="showModalEntry"
      :show="showModalEntry"
      @onBack="showModalEntry=false"
      @save="onBillingEntry()"
      :mode="entryMode"
      :monthId="filters.billing.monthId"
      :year="filters.billing.year"
      :form="forms.billing" />

    <BatchBillingEntry 
      :show="showModalBatchEntry"
      @close="showModalBatchEntry=false" 
      @save="onBatchBillingEntry()"
      :monthId="filters.billing.monthId"
      :year="filters.billing.year"
      :form="forms.batchBilling" />

    <DeleteConfirmation
      @close="showModalConfirmation = false"
      @yes="onBillingDelete()"
      :show="showModalConfirmation"
      :isProcessing="forms.billing.isProcessing"
      name="Billing" />

    <SettingModal 
      :show="showSettingModal"
      @close="showSettingModal = false"
    />

    <AuthorizationModal
      @authorize="onAuthorize"
      :show="showModalAuthorization"
      @close="showModalAuthorization = false"
    />
  </div>
</template>

<script>
import { CustomToolTip, CustomCard, CustomModal, DeleteConfirmation, SettingModal, AuthorizationModal } from "../../components";
import BillingEntry from './BillingEntry'
import BatchBillingEntry from './BatchBillingEntry'
import { MonthPickerInput } from 'vue-month-picker'
import { BillingApi, BillingPeriodApi, ClosedBillingPeriodApi, ReportApi } from '../../../mixins/api';
import { Months, BillingStatuses, BillingPermissions } from "../../../helpers/enum";
import Full from "../../components/Full";
import { clearFields, formatNumber, reset, showNotification, validate } from '../../../helpers/forms';
import { copyValue } from '../../../helpers/extractor';
import BillingRowDetail from '../../components/BillingRowDetail'
import Access from '../../../mixins/utils/Access'
import Settings from '../../../mixins/utils/Settings';

const billingFields = {
  id: null,
  billingNo: null,
  clientId: null,
  contractId: null,
  billingDate: null,
  dueDate: null,
  year: null,
  monthId: null,
  charges: null,
  adjustmentCharges: null,
  creditMemoIds: null,
  cutoffDay: null
}

const batchBillingFields = {
  billingDate: null,
  dueDate: null,
  year: null,
  monthId: null,
  cutoffDay: null,
  taxTypeId: null,
  businessTypeId: null,
  businessStyleId: null
}

export default {
  components: {
    Full,
    BillingEntry,
    MonthPickerInput,
    CustomToolTip,
    CustomCard,
    DeleteConfirmation,
    BillingRowDetail,
    BatchBillingEntry,
    CustomModal,
    AuthorizationModal,
    SettingModal
  },
  mixins: [ BillingApi, ReportApi, BillingPeriodApi, Access, Settings ],
  BillingStatuses,
  Months,
  BillingPermissions,
  data() {
    return {
      rowLoading: false,
      showModalEntry: false,
      showModalBatchEntry: false,
      showSettingModal: false,
      entryMode: 'Add',
      showModalConfirmation: false,
      showModalAuthorization: false,
      billingPeriodStatus: true,
      isProcessing: false,
      forms: {
        billing: {
          isProcessing: false,
          fields: { ...billingFields },
          errors: { ...billingFields },
          states: { ...billingFields }      
        },
        batchBilling: {
          isProcessing: false,
          fields: { ...batchBillingFields },
          errors: { ...batchBillingFields },
          states: { ...batchBillingFields }
        }
      },
      tables: {
        billings: {
          isBusy: false,
          fields: [
            {
              key: 'billingNo',
              label: 'Billing No',
              thClass: 'align-middle',
              thStyle: { minWidth: '150px' },
              sortable: true
            },
            {
              key: 'client',
              label: 'CLIENT',
              thClass: 'align-middle',
              thStyle: { width: '18%' },
            },
            {
              key: 'contract',
              label: 'CONTRACT',
              thClass: 'align-middle',
              thStyle: { width: '18%' },
            },
            {
              key: 'billingDate',
              label: 'BILLING DATE',
              thClass: 'align-middle',
              thStyle: { width: '12%' },
            },
            {
              key: 'dueDate',
              label: 'DUE DATE',
              thClass: 'align-middle',
              thStyle: { width: '12%' },
            },
            {
              key: 'period',
              label: 'PERIOD',
              thClass: 'align-middle',
              thStyle: { width: '13%' },
            },
            {
              key: 'amount',
              label: 'AMOUNT',
              thClass: 'align-middle text-right',
              tdClass: 'text-right',
              thStyle: { width: '13%' },
              formatter: (value) => {
                return formatNumber(value)
              }
            },
            {
              key: 'action',
              label: '',
              thStyle: { maxWidth: '50px' },
              thClass: 'align-middle',
            },
          ],
          items: [],
        }
      },
      paginations: {
        billing: {
          from: 0,
          to: 0,
          totalRows: 0,
          page: 1,
          perPage: 10,
        }
      },
      filters: {
        billing: {
          criteria: null,
          monthId: new Date().getMonth() + 1,
          year: new Date().getFullYear(),
          sortBy: 'billing_no',
          sortDesc: true
        }
      },
      options: {
        billingPeriods: {
          isBusy: false,
          items: []
        }
      },
      selectedBillingPeriod: null,
      row: null
    }
  },
  created() {
    // this.loadBillings()
    this.loadBillingPeriods()
  },
  methods: {
     sortChanged(event) {
      const { billing } = this.filters
      billing.sortBy =  event.sortBy
      billing.sortDesc = event.sortDesc
      this.loadBillings()
    },
    loadBillings() {
      const { billings } = this.tables
      const { billing, billing: { perPage, page } } = this.paginations
      const { monthId, year, criteria, sortBy, sortDesc } = this.filters.billing

      const isDesc = sortDesc ? 'DESC' : 'ASC'
      const sortKey = sortBy === 'billingNo' ? 'billing_no' : 'id';
      const params = { paginate: true, perPage, page, monthId, year, criteria, filterByUser: true, sortKey, sortDesc: isDesc }


      billings.isBusy = true
      this.getBillingList(params).then(( { data } ) => {
        billings.items = data.data
        billing.from = data.meta.from
        billing.to = data.meta.to
        billing.totalRows = data.meta.total
        billings.isBusy = false
      })
    },
   
    loadBillingPeriods() {
      const { billingPeriods } = this.options
      const { billing } = this.filters
      const params = { paginate: false }
      billingPeriods.isBusy = true
      this.getBillingPeriodList(params)
      .then(({ data }) => {
        billingPeriods.items = data
        const activeBillingPeriod = data.find(d => d.isActive)
        billing.year = activeBillingPeriod.year
        billing.monthId = activeBillingPeriod.monthId
        this.billingPeriodStatus = activeBillingPeriod.isActive
        this.selectedBillingPeriod = activeBillingPeriod

        this.loadBillings()
        billingPeriods.isBusy = false
      })
    },
    setCreate() {
      const { billing, billing: { fields } } = this.forms
      clearFields(billing)
      reset(billing)
      fields.cutoffDay = this.currentCutOffDay //set cutoff day

      fields.adjustmentCharges = []
      fields.charges = []
      // fields.billingDate = new Date()
      // fields.dueDate = new Date()
      this.entryMode = "Add"
      this.showModalEntry = true
    },
    setUpdate(row, authorized = false) {
      if (!this.billingPeriodStatus && !authorized) {
        this.row = row
        this.showModalAuthorization = true
        return
      }
      const { billing, billing: { fields } } = this.forms
      clearFields(billing)
      reset(billing)
      fields.adjustmentCharges = []
      fields.charges = []
      this.getBilling(row.id).then(( { data }) => {
        copyValue(data, fields)
        fields.cutoffDay = this.currentCutOffDay //set cutoff day
        this.entryMode = "Edit"
        this.showModalEntry = true
        // services.items = data.services
        // charges.items = data.charges
      })
    },
    setDelete(id) {
      const { fields } = this.forms.billing
      fields.id = id
      this.showModalConfirmation = true
    },
    onBillingEntry() {
      const {
        billing,
        billing: {
          fields: {
            billingNo,
            client,
            charges,
            adjustmentCharges,
            ...fields
          }
        }
      } = this.forms
      billing.isProcessing = true
      const { year, monthId } = this.filters.billing
      const adjustmentChargeData = adjustmentCharges.map(c => {
        return {
          chargeId: c.id,
          amount: c.pivot.amount,
          notes: c.pivot.notes
        }
      })
      const chargeData = charges.map(c => {
        return {
          chargeId: c.id,
          amount: c.pivot.amount,
          notes: c.pivot.notes
        }
      })
      const data = {
        ...fields,
        year,
        monthId,
        adjustmentCharges: adjustmentChargeData,
        charges: chargeData
      }
      reset(billing)
      if (this.entryMode === 'Add') {
        this.addBilling(data).then(({ data }) => {
          this.showModalEntry = false
          showNotification(this, "success", "Billing created successfully.")
          billing.isProcessing = false
          this.loadBillings()
        })
        .catch(error => {
          const errors = error.response.data.errors
          billing.isProcessing = false
          validate(billing, errors)
        })
      } else {
        this.updateBilling(data, fields.id).then(({ data }) => {
          billing.isProcessing = false
          this.showModalEntry = false
          showNotification(this, "success", "Billing updated successfully.")
          this.loadBillings()
        })
        .catch(error => {
          const errors = error.response.data.errors
          billing.isProcessing = false
          validate(billing, errors)
        })
      }
    },
    onBatchBillingEntry() {
      const { 
        batchBilling,
        batchBilling: { 
          fields
        } 
      } = this.forms
      batchBilling.isProcessing = true
      const { year, monthId } = this.filters.billing
      const data = {
        ...fields,
        year,
        monthId,
        filterByUser: true
      }
      reset(batchBilling)
      this.batchBilling(data).then(({ data }) => {
        this.showModalBatchEntry = false
        showNotification(this, "success", "Batch Billing created successfully.")
        batchBilling.isProcessing = false
        this.loadBillings()
      })
      .catch(error => {
        const errors = error.response.data.errors
        batchBilling.isProcessing = false
        validate(batchBilling, errors)
      })
    },
    onBillingDelete() {
      const { billing, billing: { fields: { id } } } = this.forms
      billing.isProcessing = true
      this.deleteBilling(id)
        .then(({ data }) => {
          this.loadBillings()
          billing.isProcessing = false
          showNotification(this, "success", "Billing deleted successfully.")
          this.showModalConfirmation = false
        })
        .catch(error => {
          const errors = error.response.data.errors
          billing.isProcessing = false
          if(errors.msg) {
            showNotification(this, "danger", errors.msg)
          }
        })
    },
    onToggleDetails(row) {
      const billingId = row.item.id
      if(!row.detailsShowing) {
        this.rowLoading = true
        this.getBilling(billingId).then(({ data }) => {
          copyValue(data, row.item)
          this.$set(row.item, 'charges', data.charges)
          this.$set(row.item, 'adjustmentCharges', data.adjustmentCharges)
          // this.$set(row.item, 'bank', data.bank)
          this.rowLoading = false 
        })
      }
      row.toggleDetails()
    },
    onPreview(billingId) {
      this.previewBillingStatement(billingId).then(({ data }) => {
        // console.log(response)
        //Create a Blob from the PDF Stream
        const file = new Blob([data], { type: "application/pdf" });
        //Build a URL from the file
        const fileURL = URL.createObjectURL(file);
        //Open the URL on new Window
        window.open(fileURL);
      })
      
    },
    setCreateBatchBilling(){
      const { batchBilling, batchBilling: { fields } } = this.forms
      clearFields(batchBilling)
      reset(batchBilling)
      fields.taxTypeId = null
      fields.businessTypeId = null
      fields.businessStyleId = null
      fields.cutoffDay = this.currentCutOffDay //set cutoff day
      this.showModalBatchEntry = true
    },
    onChangeBillingPeriod(billingPeriod) {
      if (billingPeriod) {
        const { billing } = this.filters
        billing.year = billingPeriod.year
        billing.monthId = billingPeriod.monthId
        this.billingPeriodStatus = billingPeriod.isActive
        this.loadBillings()
      }
    },
    filterBillingPeriod(option, label, search) {
      let temp = search.toLowerCase();
      return option.month.name.toLowerCase().indexOf(temp) > -1 || 
      String(option.year).toLowerCase().indexOf(temp) > -1
    },
    onAuthorize(isAuthorized) {
      if (isAuthorized) {
        showNotification(this, 'success', "Authorized successfully.")
        this.showModalAuthorization = false
        this.setUpdate(this.row, isAuthorized)
      } else {
        showNotification(this, 'danger', "Sorry but this user isn't authorized.")
      }
      
    }
  },
  computed: {
    defaultMonth() {
      return new Date().getMonth() + 1
    },
    defaultYear() {
      return new Date().getFullYear()
    },
    currentCutOffDay() {
      return this.$store.state.systemSetting.billingCutoffDay || 1
    }
  }
}
</script>

<style lang="scss" scoped>
  .month-filter {
    display: flex;
    align-items: center;
  }

  .row__sub-label {
    font-size: 8pt!important;
    color: gray!important;
  }

  .billing-period__status {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 25px;
    width: 100px;
    margin-right: 5px;
  }

  .billing-period__select {
    width: 180px;
  }

</style>
